import { User, LoginMethod, BuyerUserRole } from '@choco-admin/types';

import {
  UsersPageUserDetailsFragmentFragment,
  UsersPageUserSearchFragmentFragment,
} from '../../../generated';

// Union type for all user fragments
export type GeneratedUserFragment =
  | UsersPageUserDetailsFragmentFragment
  | UsersPageUserSearchFragmentFragment;

/**
 * Maps a GraphQL user fragment to our User type
 * This mapper handles both search and details fragments
 */
export const mapToUser = (user: GeneratedUserFragment): User => {
  const isDetailedUser = 'isTest' in user;
  const profileImage = 'profileImage' in user ? user.profileImage : undefined;

  return {
    id: user.id,
    createdAt: (user.createdAt || new Date().toISOString()).toString(),
    updatedAt: new Date().toISOString(),

    // Core user fields from GraphQL schema
    name: user.name ?? undefined,
    email: user.email ?? undefined,
    phone: user.phone ?? undefined,
    locale: user.locale ?? undefined,
    loginMethod: user.loginMethod as LoginMethod | undefined,
    pending: user.pending ?? undefined,

    // User type flags
    isTest: isDetailedUser ? (user.isTest ?? undefined) : undefined,
    supplier: user.supplier || false, // Required field in GraphQL schema
    isPuppy: 'isPuppy' in user ? (user.isPuppy ?? undefined) : undefined,
    hasRequestedToBeDeleted:
      'hasRequestedToBeDeleted' in user
        ? (user.hasRequestedToBeDeleted ?? undefined)
        : undefined,

    // Support login (chocoUser in GraphQL)
    supportLoginEnabled:
      'chocoUser' in user ? (user.chocoUser ?? undefined) : undefined,

    // Linked supplier data
    supplierId: user.linkedSupplier?.id,
    supplierName: user.linkedSupplier?.name,
    supplierReferenceId:
      user.linkedSupplier && 'referenceId' in user.linkedSupplier
        ? user.linkedSupplier.referenceId
        : undefined,

    // Buyer relationships
    linkedBuyers:
      user.buyers?.edges?.map((edge) => ({
        id: edge.node.id,
        name: edge.node.name,
        referenceId: edge.node.referenceId ?? undefined,
        fullAddress: edge.node.fullAddress ?? undefined,
        role: (edge.role as BuyerUserRole) ?? BuyerUserRole.ADMIN, // Role from GraphQL edge
        rolesEnabled: edge.node.rolesEnabled ?? undefined,
      })) || [],

    // Order preferences
    orderPreferences: user.orderPreference
      ? {
          pdf: user.orderPreference.pdf ?? false,
          xlsx: user.orderPreference.xlsx ?? false,
          sms: user.orderPreference.sms ?? null,
          whatsApp: user.orderPreference.whatsApp ?? null,
        }
      : undefined,

    // Profile image data
    profileImageUrl: profileImage?.url ?? undefined,
    profileImageUploadUrl: profileImage?.uploadUrl ?? undefined,
    profileImageMediumSquare: profileImage?.mediumSquare ?? undefined,
  };
};
